import { useCallback, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import pluralize from "pluralize"

import { JOIN_PROFILE_PATH } from "@productlane/lib/src/join-profile"
import { Avatar, Button } from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { api } from "@/lib/api"

interface IProps {
	allowedDomain: RouterOutputs["user"]["allowedDomains"][number]
}

export function JoinWorkspaceItem({ allowedDomain }: IProps) {
	const ctx = api.useUtils()
	const navigate = useNavigate()
	const [isJoined, setIsJoined] = useState(allowedDomain.joined)

	const joinMutation = api.user.joinWorkspaceByAllowedDomain.useMutation({
		onSuccess: async () => {
			await ctx.user.allowedDomains.invalidate()
			setIsJoined(true)
			void navigate({ href: JOIN_PROFILE_PATH })
		},
	})

	const handleClick = useCallback(async () => {
		if (isJoined) {
			void navigate({ to: "/threads" })
			return
		}
		await joinMutation.mutateAsync({ allowedDomainId: allowedDomain.id })
	}, [isJoined, joinMutation, allowedDomain.id, navigate])

	return (
		<div className="flex items-center justify-between gap-3 px-4 py-3">
			<div className="flex min-w-0 items-center gap-3">
				<Avatar
					image={allowedDomain.logoUrl}
					name={allowedDomain.workspaceName}
					size="md"
					roundness="md"
					fallbackProps={{
						className:
							"bg-gray-950 text-white dark:bg-gray-50 dark:text-gray-950",
					}}
				/>
				<div className="flex min-w-0 flex-col">
					<div className="truncate text-sm font-medium capitalize text-primary">
						{allowedDomain.workspaceName}
					</div>
					<div className="text-xs text-secondary">
						{allowedDomain.memberCount}{" "}
						{pluralize("member", allowedDomain.memberCount)}
					</div>
				</div>
			</div>
			<Button
				onClick={handleClick}
				isLoading={joinMutation.isLoading}
				isRounded
				size="default"
				variant="outline"
				className="!px-5"
			>
				{isJoined ? "Open" : "Join"}
			</Button>
		</div>
	)
}
